import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { functions } from '../../Firebase';
import { Message, displayMessage } from '../ModalComponents/Spinner';
import { Link } from 'react-router-dom';

const OPEN_STATUSES = ['open', 'in_progress'];

export default function Ticket(props) {
  const endDivRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [ticketLoaded, setTicketLoaded] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const disableReplyBtn = (disable) => {
    const replyBtn = document.getElementById("replyButton");
    if (replyBtn) {
      if (disable) {
        replyBtn.classList.add("disabled");
      } else {
        replyBtn.classList.remove("disabled");
      }
    }
  };

  const scrollToBottom = () => {
    if (endDivRef?.current) {
      endDivRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  };

  const getTicketNo = () => {
    if (!ticketLoaded) {
      return "Loading...";
    }
    if (ticket) {
      return `Ticket #${ticket.id?.slice(-8) || ''}`;
    }
    return "Ticket does not exist";
  };

  const fetchTicket = async () => {
    if (!props.ticketId) return;
    try {
      const getSupportTicketById = functions.httpsCallable("getSupportTicketById");
      const result = await getSupportTicketById({ ticketId: props.ticketId });
      setTicket(result.data.ticket);
    } catch (error) {
      console.error("Error fetching ticket:", error);
      setTicket(null);
    } finally {
      setTicketLoaded(true);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [props.ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket]);

  const sendReply = async () => {
    if (!ticket || !OPEN_STATUSES.includes(ticket.status) || reply.trim() === "") return;

    disableReplyBtn(true);
    setSending(true);

    try {
      const respondToSupportTicket = functions.httpsCallable("respondToSupportTicket");
      await respondToSupportTicket({
        ticketId: ticket.id,
        message: reply.trim(),
        isAdmin: false,
      });
      setReply('');
      await fetchTicket();
    } catch (error) {
      console.error("Error sending reply:", error);
      displayMessage(true, false, error.message || 'Failed to send message. Please try again.');
      disableReplyBtn(false);
    } finally {
      setSending(false);
      disableReplyBtn(reply === "");
    }
  };

  // Build combined message list: original message + responses
  const buildMessageList = () => {
    if (!ticket) return [];

    const messages = [];

    // Original message from user
    messages.push({
      messageBody: ticket.message,
      admin: false,
      timestamp: ticket.createdAt,
    });

    // Admin/user responses
    const responses = ticket.responses || [];
    responses
      .sort((a, b) => {
        const aTime = a.createdAt?._seconds ?? a.createdAt?.seconds ?? 0;
        const bTime = b.createdAt?._seconds ?? b.createdAt?.seconds ?? 0;
        return aTime - bTime;
      })
      .forEach((r) => {
        messages.push({
          messageBody: r.message,
          admin: r.isAdmin,
          timestamp: r.createdAt,
        });
      });

    return messages;
  };

  const messageList = buildMessageList().map((data, key) => (
    <React.Fragment key={key}>
      <div className={`ticket-message ${data.admin ? '' : 'from-admin'}`}>
        <div className="message">
          {data.messageBody}
          <div className="from-who">{data.admin ? 'Admin' : 'Me'}</div>
        </div>
        {data.timestamp && (() => {
          const ts = data.timestamp;
          const date = ts.toDate ? ts.toDate() : (ts._seconds ? new Date(ts._seconds * 1000) : null);
          return date ? date.toLocaleString('en-NZ') : '';
        })()}
      </div>
    </React.Fragment>
  ));

  const canReply = ticket && OPEN_STATUSES.includes(ticket.status);

  return (
    <div className="home">
      <Helmet>
        <title>Trippie Air - Support Ticket #{ticket ? ticket.id?.slice(-8) : ''}</title>
        <meta name="description" content="Trippie Air" />
      </Helmet>
      <Message />
      <div className="home-hero bg-gradient">
        <div className="container-middle-content">
          <div className="tickets-messages-header-text">
            <h1 className="medium-heading">Support Ticket</h1>
            <p className="medium-para">
              {getTicketNo()}
              <br />
              {ticket ? ticket.reason : ''}
              {ticket && (ticket.status === 'resolved' || ticket.status === 'closed') && ticket.closedAt && (
                <>
                  <br />
                  <span style={{ fontSize: '0.9em', opacity: 0.9 }}>
                    Closing Date: {(() => {
                    const ts = ticket.closedAt;
                    const date = ts?.toDate ? ts.toDate() : (ts?._seconds ?? ts?.seconds ? new Date((ts._seconds ?? ts.seconds) * 1000) : null);
                    return date ? date.toLocaleString('en-NZ') : '';
                  })()}
                    {ticket.closedByName && ` · Closed By: ${ticket.closedByName}`}
                  </span>
                </>
              )}
            </p>
            <span style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'stretch' }}>
              <button
                type="button"
                className="standard-button invert-btn-color"
                onClick={fetchTicket}
                disabled={sending}
                style={{ margin: 0, minWidth: '140px', height: '42px', lineHeight: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}
              >
                Refresh
              </button>
              <Link to="/support/" className="standard-button invert-btn-color" style={{ margin: 0, minWidth: '140px', height: '42px', lineHeight: '42px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                My Tickets
              </Link>
            </span>
          </div>
        </div>
      </div>
      <div className="container-middle-content ">
        <div id="messageContainer" className="ticket-messages-container">
          {messageList}
          <div ref={endDivRef}></div>
        </div>
        {canReply && (
          <div className="reply-container">
            <input
              type="text"
              placeholder="Write a message"
              value={reply}
              onChange={(e) => {
                const input = e.target.value;
                disableReplyBtn(input === "");
                setReply(input);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && reply.trim() !== "") {
                  sendReply();
                }
              }}
              disabled={sending}
            />
            <button
              id="replyButton"
              className="reply-button disabled"
              onClick={() => {
                if (reply.trim() !== "") sendReply();
              }}
              disabled={sending}
            >
              <i className="far fa-paper-plane"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
